import { Grid, Paper } from '@mui/material';
import { SiTrackPage } from '../../components/si-track-page';
import InspectionsFilters from './inspections-filters';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSaveData } from '../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import Inspections from './inspections';
import InspectorsFilters from './inspectors-filters';
import { usersApiKeyStore } from '../../api/users-api-key-store';
import Inspectors from './inspectors';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { holidayManagementApiKeyStore } from '../../api/masters/holiday-management-api-key-store';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const convertToArray = (value: any): number[] => {
  if (!value) return [];

  return value
    .toString()
    .split(',')
    .map((id: string) => Number(id.trim()))
    .filter((id: any) => !isNaN(id));
};

const transformData = (data: any) => {
  return {
    customerID: data.customer?.customerID ?? null,
    divisions: data.divisions?.map((d: any) => d.divisionID) ?? [],
    departments: data.departments?.map((d: any) => d.departmentID) ?? [],
    inspectors: data.userIDs?.map((u: any) => u.userID) ?? [],
    marketAreas: data.marketAreaIDs?.map((m: any) => m.marketAreaID) ?? [],
    offices: data.officeIDs?.map((o: any) => o.officeID) ?? [],
    projects: data.projectIDs?.map((p: any) => p.projectID) ?? [],
    scheduledDateFrom: data.fromDate ?? null,
    scheduledDateTo: data.toDate ?? null,
    workOrderStatusIDs: data.workOrderStatusID?.map((d: any) => d.workOrderStatusID) ?? [],
    // workOrderStatusID: 2,
    workOrderNumber: data?.workOrderNumber ?? '',
    inspectorType: data?.inspectorType ?? 'primaryInspection',
    filterFromArea: "schedulebalancing"
  };
};

const transformDataForInspector = (data: any, inspectorData: any, inspectionType: any) => {
  return {
    skillsets: data.skillsetIDs.map((skill: any) => skill.skillsetID),
    equipments: data.equipmentIDs.map((eq: any) => eq.equipmentID),
    marketAreas: data.marketAreaIDs.map((ma: any) => ma.marketAreaID),
    inspectors: data.userIDs.map((user: any) => user.userID),
    divisions: data.divisionIDs?.map((d: any) => d.divisionID) ?? [],
    departments: data.departmentIDs?.map((d: any) => d.departmentID) ?? [],
    scheduledDateFrom: data.fromDateForInspector ?? null,
    scheduledDateTo: data.toDateForInspector ?? null,
    filterFromArea: 'schedulebalancing',
    ...(inspectionType === "primaryInspection" && {
      workOrders: inspectorData?.map((user: any) => user.workOrderID),
    })
  };
};

export const ScheduleDragAndDrop = () => {
  const [searchParams] = useSearchParams();
  const [inspectionType, setInspectionType] = useState<any>('primaryInspection');
  const [draggedInspections, setDraggedInspections] = useState<any>();
  const [inspectorCountDetails, setInspectorCount] = useState<any>();
  const { isLoading, data, mutateAsync } = useSaveData<any>(workOrdersApiKeyStore.workOrderInspections());
  const { isLoading: isInspectorCountLoading, data: inspectorCountData, mutateAsync: mutateInspectorCountAsync } = useSaveData<any>(workOrdersApiKeyStore.workOrderInspectorInspectionCounts());
  const {
    isLoading: isLoadingInspector,
    data: inspectorData,
    mutateAsync: mutateAsyncInspector,
  } = useSaveData<any>(usersApiKeyStore.updateInspectors());

  const [holidayDates, setHolidayDates] = useState<any[]>([]);

  const { isLoading: isHolidayLoading,
    data: holidayData } = useLoadData<ApiResponse<any[]>>(
      holidayManagementApiKeyStore.getHolidays());

  useEffect(() => {
    if (holidayData) {
      const datesResult = holidayData?.data
        .filter((item: any) => item.isActive)
        //?.map((item: any) => item.holidayDate);
        ?.map((item: any) => ({
          date: item.holidayDate,
          name: item.holidayName,
        }));
      setHolidayDates(datesResult);
    }
  }, [holidayData])

  useEffect(() => {
    if (inspectorCountData) {
      setInspectorCount(inspectorCountData?.data);
    }
  }, [inspectorCountData])

  const [inspectionsFilterState, setInspectionsFilterState] = useState({
    customer: Number(searchParams.get('customerID')) || '',
    officeIDs: convertToArray(searchParams.get('offices')) || [],
    projectIDs: convertToArray(searchParams.get('projects')) || [],
    marketAreaIDs: convertToArray(searchParams.get('marketAreas')) || [],
    userIDs: convertToArray(searchParams.get('inspectors')) || [],
    workOrderStatusID: convertToArray(searchParams.get('workOrderStatusID')) || [],
    fromDate: searchParams.get('dateFrom') ?? '',
    toDate: searchParams.get('dateTo') ?? '',
    divisions: convertToArray(searchParams.get('divisions')) || [],
    departments: convertToArray(searchParams.get('departments')) || [],
    workOrderNumber: searchParams.get('workOrderNumber') ?? '',
    inspectorType: searchParams.get('inspectorType') ?? 'primaryInspection',
  });

  const [inspectorFilter, setInspectorFilter] = useState({
    userIDs: [] as number[],
    marketAreaIDs: [] as number[],
    skillsetIDs: [] as number[],
    equipmentIDs: [] as number[],
    divisionIDs: [] as number[],
    fromDateForInspector: '',
    toDateForInspector: '',
  });

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: inspectionsFilterState.customer, customerName: '' },
      officeIDs: inspectionsFilterState.officeIDs,
      projectIDs: inspectionsFilterState.projectIDs,
      marketAreaIDs: inspectionsFilterState.marketAreaIDs,
      userIDs: inspectionsFilterState.userIDs,
      workOrderStatusID: inspectionsFilterState?.workOrderStatusID,
      fromDate: inspectionsFilterState.fromDate,
      toDate: inspectionsFilterState.toDate,
      divisions: inspectionsFilterState.divisions,
      departments: inspectionsFilterState.departments,
      workOrders: [],
      keepScheduledDateasOriginal: false,
      workOrderNumber: inspectionsFilterState.workOrderNumber,
      inspectorType: inspectionsFilterState.inspectorType,
      proposedScheduledDate: ""
    },
  });

  const handleInspectionsFilterClick = () => {
    const { inspectorType, ...filteredPayload } = transformData(inspectionsFilterState);
    const payload = {
      ...filteredPayload,
      isPrimaryInspector: inspectionsFilterState.inspectorType == "primaryInspection",
      isSecondaryInspector: inspectionsFilterState.inspectorType == "secondaryInspection"
    }
    mutateAsync({ data: payload });
    setDraggedInspections([]);
    if (payload?.inspectors?.length > 0) {
      mutateInspectorCountAsync({ data: payload });
    }
    else {
      setInspectorCount(null);
    }
  };

  const handleInspectorFilterClick = () => {
    const filteredPayload = transformDataForInspector(inspectorFilter, draggedInspections, inspectionType);
    mutateAsyncInspector({ data: filteredPayload });
  };

  const handleInspectorRefetch = (inspectorData: any) => {
    const filteredPayload = transformDataForInspector(inspectorFilter, inspectorData, inspectionType);
    mutateAsyncInspector({ data: filteredPayload });
  };

  const handleSelectedInspections = (values: any) => {
    setDraggedInspections(values ?? []);
  }

  const handleInspectionType = (value: any) => {
    setInspectionType(value);
  }

  const handleSubmit = async (data: any) => { };

  return (
    <SiTrackPage title="Schedule Balancing">
      <div className="innerContent noOuterBg">
        <Paper elevation={0} style={{ paddingLeft: '16px' }}>
          <ReactHookForm
            formName="BULK_SCHEDULING_FORM"
            onSubmit={handleSubmit}
            formContextProps={form}
          // isBusy={isLoading}
          >
            <Grid
              // container
              spacing={1.5}
              style={{ background: 'none', padding: '0', width: '100%' }}
            >
              <LoadingIndicator isLoading={isLoading || isLoadingInspector || isInspectorCountLoading} />
              <Grid item lg={12} sm={12} xl={12} xs={12} style={{ paddingTop: '24px' }}>
                <Grid container spacing={1} style={{ marginLeft: '0', marginRight: '0', width: '100%' }}>
                  <Grid container spacing={1} style={{ marginLeft: '0', marginRight: '0', width: '100%' }}>
                    <Grid
                      container
                      spacing={1}
                      lg={7.5}
                      sm={7.5}
                      xl={7.5}
                      xs={7.5}
                      style={{
                        marginLeft: '0',
                        marginRight: '0',
                        paddingLeft: '8px',
                        paddingRight: '16px',
                        borderRight: '2px solid #e2e2e2',
                      }}
                    >
                      <h3 style={{ width: '100%', marginBottom: '15px' }}>Inspection Filters</h3>
                      <Grid container spacing={1} lg={12} sm={12} xl={12} xs={12}>
                        <InspectionsFilters
                          holidayDates={holidayDates}
                          isParentLoading={isHolidayLoading}
                          inspectionsFilterState={inspectionsFilterState}
                          setInspectionsFilterState={setInspectionsFilterState}
                          handleInspectionsFilterClick={handleInspectionsFilterClick}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={1}
                      lg={4.5}
                      sm={4.5}
                      xl={4.5}
                      xs={4.5}
                      style={{
                        paddingLeft: '16px',
                        paddingRight: '8px',
                        marginLeft: '0',
                        marginRight: '0',
                        display: 'block',
                      }}
                    >
                      <h3 style={{ width: '100%', marginBottom: '15px' }}>Inspector Filters</h3>
                      <Grid container spacing={1} lg={12} sm={12} xl={12} xs={12}>
                        <InspectorsFilters
                          holidayDates={holidayDates}
                          isParentLoading={isHolidayLoading}
                          handleInspectorFilterClick={handleInspectorFilterClick}
                          inspectorFilter={inspectorFilter}
                          setInspectorFilter={setInspectorFilter}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <DndProvider backend={HTML5Backend}>
                    <Grid
                      className="inspection-inspectorListWrapper"
                      item
                      lg={12}
                      sm={12}
                      xl={12}
                      xs={12}
                      style={{ padding: '8px' }}
                    >
                      <div
                        className="inspection-inspectorListContainer"
                        style={{
                          padding: '0',
                          background: '#fff',
                          borderRadius: '8px',
                          display: 'flex',
                          gap: '0px',
                          width: '100%', // Space between the sections
                        }}
                      >
                        {/* Inspections on the left */}

                        <Grid
                          className="inspections-listContainer"
                          sm={7.5}
                          container
                          spacing={1}
                          style={{
                            flex: 1,
                            borderRight: '2px solid #e2e2e2',
                            margin: 0,
                            padding: ' 16px 16px 16px 32px',
                            minHeight: '300px',
                            justifyContent: 'center',
                          }}
                        >
                          <Inspections
                            inspections={data?.data}
                            inspectorCount={inspectorCountDetails}
                            setSelectedInpsections={handleSelectedInspections}
                            handleInspectionsFilterClick={handleInspectionsFilterClick}
                          />
                        </Grid>
                        {/* Vertical Divider */}

                        {/* Inspectors on the right */}
                        <Grid
                          className="inspectors-listContainer"
                          sm={4.5}
                          container
                          spacing={1}
                          style={{
                            flex: 1,
                            marginLeft: 0,
                            padding: '  16px 16px 16px 32px',
                            justifyContent: 'center',
                            marginTop: '0',
                          }}
                        >
                          <Inspectors
                            inspectors={inspectorData?.data}
                            handleInspectorFilterClick={handleInspectorRefetch}
                            handleInspectionsFilterClick={handleInspectionsFilterClick}
                            passInspectionType={handleInspectionType}
                          />
                        </Grid>
                      </div>
                    </Grid>
                  </DndProvider>
                </Grid>
              </Grid>
            </Grid>
          </ReactHookForm>
        </Paper>
      </div>
    </SiTrackPage>
  );
};
